import { Id, Identifiable, List, Task } from "@planzone/dto";
import { events } from "@planzone/events";
import { Boundaries } from "@planzone/core";
import { resizeList } from "./utils/resizeList";
import { reduceTask } from "../task";

function editTasks(
  list: Identifiable<List>,
  data: Boundaries["markTaskAsCompleted"]["success"],
) {
  return list.tasks.reduce<{
    hasMatch: boolean;
    tasks: Identifiable<Task>[];
  }>(
    ({ hasMatch, tasks }, t) => ({
      hasMatch: hasMatch || t.uuid === data.task.uuid,
      tasks: [
        ...tasks,
        reduceTask(t, { type: events.markTaskAsCompletedSucceeded, data }),
      ],
    }),
    { hasMatch: false, tasks: [] },
  );
}

function editSubLists(
  list: Identifiable<List>,
  task: Boundaries["markTaskAsCompleted"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
  }[],
) {
  return list.subLists.reduce<{
    hasMatch: boolean;
    subLists: Identifiable<List>[];
  }>(
    ({ hasMatch, subLists }, subList) => {
      const { hasMatch: hasSubListMatch, list: nextSubList } = editList(
        subList,
        task,
        impactedLists,
      );

      return {
        hasMatch: hasMatch || hasSubListMatch,
        subLists: [...subLists, nextSubList],
      };
    },
    { hasMatch: false, subLists: [] },
  );
}

function editList(
  list: Identifiable<List>,
  data: Boundaries["markTaskAsCompleted"]["success"],
  impactedLists: {
    uuid: Id;
    endDate: string;
    startDate: string;
  }[],
): { hasMatch: boolean; list: Identifiable<List> } {
  const { hasMatch, tasks } = editTasks(list, data);
  const impactedList = impactedLists.find(({ uuid }) => uuid === list.uuid);
  const { hasMatch: hasSubListMatch, subLists } = editSubLists(
    list,
    data,
    impactedLists,
  );
  const nextList = { ...list, ...(impactedList || {}), tasks, subLists };
  const isMatching = hasMatch || hasSubListMatch;

  return {
    hasMatch: isMatching,
    list: isMatching ? resizeList(nextList, data.task.endDate) : nextList,
  };
}

function handleMarkTaskAsCompleted(
  list: Identifiable<List>,
  data: Boundaries["markTaskAsCompleted"]["success"],
): Identifiable<List> {
  const impactedLists = data.impactedElements
    ? data.impactedElements.filter(({ type }) => type === "list")
    : [];

  return editList(list, data, impactedLists).list;
}

export { handleMarkTaskAsCompleted };
